import invert from "lodash/invert";
import {
  CohortFilter,
  isAbsoluteTimeWindow,
  isDefaultLookbackWindow,
  isRelativeTimeWindow,
  LookbackWindow,
  LookbackWindowOption,
} from "./types";
import { DashboardOptions } from "src/graphql";
import { differenceInHours } from "date-fns";
import { isPresent } from "src/types/utils";

const SECONDS_IN_ONE_DAY = 86400;

export const lookbackWindowOptions = [
  { label: "Default", value: LookbackWindowOption.Default },
  { label: "7d", value: LookbackWindowOption.SevenDays },
  { label: "30d", value: LookbackWindowOption.ThirtyDays },
  { label: "60d", value: LookbackWindowOption.SixtyDays },
  { label: "90d", value: LookbackWindowOption.NinetyDays },
];

export const lookbackWindowOptionsWithoutDefaultOption =
  lookbackWindowOptions.filter(
    ({ value }) => value !== LookbackWindowOption.Default,
  );

export const lookbackWindowOptionToSeconds: Record<
  LookbackWindowOption,
  number | null
> = {
  [LookbackWindowOption.Default]: null,
  [LookbackWindowOption.SevenDays]: SECONDS_IN_ONE_DAY * 7,
  [LookbackWindowOption.ThirtyDays]: SECONDS_IN_ONE_DAY * 30,
  [LookbackWindowOption.SixtyDays]: SECONDS_IN_ONE_DAY * 60,
  [LookbackWindowOption.NinetyDays]: SECONDS_IN_ONE_DAY * 90,
};

export const secondsToLookbackWindowOptions = invert(
  lookbackWindowOptionToSeconds,
);

const friendlyLookbackWindowOptions: Record<
  LookbackWindowOption,
  string | null
> = {
  [LookbackWindowOption.Default]: null,
  [LookbackWindowOption.SevenDays]: "Last 7 days",
  [LookbackWindowOption.ThirtyDays]: "Last 30 days",
  [LookbackWindowOption.SixtyDays]: "Last 60 days",
  [LookbackWindowOption.NinetyDays]: "Last 90 days",
};

export const getRelativeTimeWindow = (data: any) => {
  if (isDefaultLookbackWindow(data)) {
    return LookbackWindowOption.Default;
  }

  if (isRelativeTimeWindow(data)) {
    return secondsToLookbackWindowOptions[data.lookback];
  }

  // Intentionally set this to a toggle value that doesn't exist so it gets cleared.
  // TODO(nishad): There might be a bug in HTUI where setting this to undefined doesn't actually clear it.
  return "null";
};

export const getAbsoluteTimeWindow = (data: any) => {
  if (isAbsoluteTimeWindow(data)) {
    return [new Date(data.start), new Date(data.end)];
  }

  return [];
};

export const getAbsoluteTimeWindowLabel = (data: any) => {
  if (!isAbsoluteTimeWindow(data)) {
    return "Custom";
  }

  return `${new Date(data.start).toLocaleDateString()} - ${new Date(data.end).toLocaleDateString()}`;
};

export const cohortFilterHasConditions = (
  cohortFilter: CohortFilter | undefined,
): cohortFilter is NonNullable<CohortFilter> => {
  if (!cohortFilter || !cohortFilter.conditions) {
    return false;
  }

  if (
    cohortFilter.conditions[0] &&
    "conditions" in cohortFilter.conditions[0] &&
    cohortFilter.conditions[0].conditions.length > 0
  ) {
    return true;
  }

  return false;
};

export const getDashboardOptions = ({
  cohortFilter,
  lookbackWindow,
  useSampledModels,
}: {
  cohortFilter: CohortFilter;
  lookbackWindow: LookbackWindow;
  useSampledModels: boolean;
}): DashboardOptions => {
  return {
    // Backend expects an object with a `conditions` field (Pick<VisualQueryFilter, "conditions">),
    // not the raw conditions array. Wrap when present.
    // Longer term, we should fix this in the backend - there's no need to use Pick instead
    // of VisualQueryFilter["conditions"] directly.
    cohortFilter: cohortFilter
      ? { conditions: cohortFilter.conditions }
      : undefined,
    lookbackWindow: isRelativeTimeWindow(lookbackWindow)
      ? lookbackWindow.lookback
      : undefined,
    timeWindow: isAbsoluteTimeWindow(lookbackWindow)
      ? lookbackWindow
      : undefined,
    useSampledModels,
  };
};

export const isAnyChartCardDataOutOfSync = (
  chartCardData: (
    | { updatedAt: Date | number | string | null; data: any }
    | null
    | undefined
  )[],
  dashboardRunAt: Date,
): boolean => {
  return chartCardData
    .filter(isPresent)
    .some(
      ({ updatedAt, data }) =>
        updatedAt &&
        data &&
        differenceInHours(new Date(updatedAt), dashboardRunAt) >= 24,
    );
};

export const friendlyDashboardLookbackWindow = ({
  lookbackWindow,
  timeWindow,
}: Pick<DashboardOptions, "lookbackWindow" | "timeWindow">): string | null => {
  if (lookbackWindow) {
    const option = secondsToLookbackWindowOptions[lookbackWindow];
    return option ? friendlyLookbackWindowOptions[option] : null;
  } else if (timeWindow) {
    return getAbsoluteTimeWindowLabel(timeWindow);
  }

  return null;
};

export const friendlyChartLookbackWindow = (
  window: LookbackWindow,
): string | null => {
  if (isAbsoluteTimeWindow(window)) {
    return getAbsoluteTimeWindowLabel(window);
  }

  if (window && "lookback" in window) {
    return friendlyLookbackWindowOptions[window.lookback];
  }

  return null;
};
